import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as Icons from 'lucide-react';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { billingService } from '../services/api';
import toast from 'react-hot-toast';
import { formatCurrencyCOP, formatCurrencyAbbreviated } from '../utils/formatters';
import BillingTrendsTab from './BillingTrendsTab';

const MonthlyLogView = ({ data, loading, onViewDate, selectedMonth, setSelectedMonth, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-12 space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-500 font-medium">Cargando bitácora mensual...</p>
      </div>
    );
  }

  const totalMonth = data.reduce((sum, d) => sum + parseFloat(d.total_amount), 0);
  const totalOrders = data.reduce((sum, d) => sum + d.total_orders, 0);

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-50 overflow-hidden">
      {/* Monthly Toolbar */}
      <div className="p-4 bg-white border-b border-gray-200 flex items-center justify-between shadow-sm">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
            <Icons.Calendar className="w-5 h-5 text-indigo-600" />
            <input 
              type="month" 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-transparent border-none text-sm font-bold focus:ring-0 outline-none cursor-pointer"
            />
          </div>
          <button 
            onClick={onRefresh}
            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <Icons.RefreshCw className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center space-x-8">
          <div className="text-right">
            <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest">Facturación Mes</p>
            <p className="text-xl font-black text-indigo-700 leading-none">${formatCurrencyCOP(totalMonth)}</p>
          </div>
          <div className="text-right border-l border-gray-200 pl-8">
            <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest">Pedidos Mes</p>
            <p className="text-xl font-black text-gray-900 leading-none">{totalOrders}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-[11px] font-black text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <th className="px-6 py-4">Fecha</th>
                <th className="px-6 py-4">Estado</th>
                <th className="px-6 py-4">Top Facturador</th>
                <th className="px-6 py-4 text-center">Pedidos</th>
                <th className="px-6 py-4 text-right">Total Facturado</th>
                <th className="px-6 py-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                    No hay registros de facturación para este mes.
                  </td>
                </tr>
              ) : (
                data.map((day) => (
                  <tr key={day.date} className="hover:bg-indigo-50/30 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900 text-sm italic capitalize">{format(new Date(day.date + 'T12:00:00'), 'EEEE d', { locale: es })}</span>
                        <span className="text-[10px] text-gray-400 font-medium capitalize">{format(new Date(day.date + 'T12:00:00'), 'MMMM yyyy', { locale: es })}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {day.is_closed ? (
                        <div className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-[10px] font-black uppercase border border-green-200">
                          <Icons.Lock className="w-3 h-3" />
                          <span>Cerrado</span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-[10px] font-black uppercase border border-amber-200">
                          <Icons.Unlock className="w-3 h-3" />
                          <span>Pendiente</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold">
                          {day.top_biller.charAt(0)}
                        </div>
                        <span className="text-sm font-medium text-gray-700">{day.top_biller}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-bold text-gray-900 bg-gray-100 px-2.5 py-1 rounded-lg">{day.total_orders}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-black text-indigo-600">${formatCurrencyCOP(day.total_amount)}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button 
                        onClick={() => onViewDate(day.date)}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                        title="Ver detalle de auditoría"
                      >
                        <Icons.Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const DELIVERY_CONFIG = {
  recoge_bodega: { label: 'BODEGA', icon: '🏢', color: 'bg-amber-50 border-amber-300 text-amber-900', badge: 'bg-amber-600' },
  domicilio: { label: 'DOMICILIO', icon: '🚚', color: 'bg-blue-50 border-blue-300 text-blue-900', badge: 'bg-blue-600' },
  nacional: { label: 'NACIONAL', icon: '📦', color: 'bg-purple-50 border-purple-300 text-purple-900', badge: 'bg-purple-600' },
  recoge_posterior: { label: 'RECOGE DESPUÉS', icon: '🕐', color: 'bg-teal-50 border-teal-300 text-teal-900', badge: 'bg-teal-600' },
  pos: { label: 'POS', icon: '🏪', color: 'bg-green-50 border-green-300 text-green-900', badge: 'bg-green-600' },
  _default: { label: 'OTRO', icon: '📋', color: 'bg-gray-50 border-gray-300 text-gray-900', badge: 'bg-gray-600' },
};

const getDeliveryConfig = (method) => {
  return DELIVERY_CONFIG[method] || DELIVERY_CONFIG._default;
};

const BillingHistoryTab = ({ user }) => {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [history, setHistory] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closingInProgress, setClosingInProgress] = useState(false);
  const [stats, setStats] = useState({
    total_orders: 0,
    total_payments_count: 0,
    total_invoiced_amount: 0,
    total_payments_amount: 0,
    total_amount: 0,
    missing_receipts: 0,
    closed: false
  });
  const [filterDelivery, setFilterDelivery] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [receiptValue, setReceiptValue] = useState('');
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [activeTab, setActiveTab] = useState('audit'); // 'audit', 'trends' or 'log'
  const [isMassiveClosing, setIsMassiveClosing] = useState(false);
  const [monthlyLog, setMonthlyLog] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [loadingLog, setLoadingLog] = useState(false);

  const getStatusDisplay = (status, deliveryMethod) => {
    const s = String(status || '').toLowerCase();
    const dm = String(deliveryMethod || '').toLowerCase();
    
    if (s === 'entregado_cliente' || s === 'entregado') {
      return { label: 'ENTREGADO', color: 'bg-green-600 text-white' };
    }
    if (s === 'en_reparto') {
      return { label: 'EN REPARTO', color: 'bg-orange-500 text-white' };
    }
    if (s === 'entregado_transportadora') {
      return { label: 'EN TRANSPORTADORA', color: 'bg-blue-600 text-white' };
    }
    if (s === 'listo_para_entrega') {
      return { 
        label: dm === 'recoge_bodega' ? 'LISTO EN BODEGA' : 'LISTO PARA ENVÍO', 
        color: 'bg-indigo-100 text-indigo-800 border border-indigo-200' 
      };
    }
    if (s === 'pendiente_por_facturacion') {
      return { label: 'PTE FACTURACIÓN', color: 'bg-yellow-100 text-yellow-800 border border-yellow-200' };
    }
    return { 
      label: s.replace(/_/g, ' ').toUpperCase(), 
      color: 'bg-gray-100 text-gray-700 border border-gray-200' 
    };
  };

  const getPaymentDisplay = (paymentMethod) => {
    const method = String(paymentMethod || '').toLowerCase();
    if (method.includes('transferencia') || method.includes('banco')) {
      return { label: 'TRANSFERENCIA', icon: Icons.CreditCard, color: 'text-purple-700 bg-purple-50 border-purple-100' };
    }
    if (method.includes('efectivo')) {
      return { label: 'EFECTIVO', icon: Icons.Banknote, color: 'text-emerald-700 bg-emerald-50 border-emerald-100' };
    }
    if (method.includes('credito') || method.includes('crédito')) {
      return { label: 'CRÉDITO', icon: Icons.Clock, color: 'text-amber-700 bg-amber-50 border-amber-100' };
    }
    if (method.includes('contraentrega')) {
      return { label: 'CONTRAENTREGA', icon: Icons.Wallet, color: 'text-orange-700 bg-orange-50 border-orange-100' };
    }
    return { label: method.toUpperCase() || 'SIN DEFINIR', icon: Icons.DollarSign, color: 'text-blue-700 bg-blue-50 border-blue-100' };
  };

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await billingService.getHistory(date);
      if (response.success) {
        setHistory(response.data.orders || []);
        setPayments(response.data.payments || []);
        setStats({
          total_orders: response.data.stats.total_orders,
          total_payments_count: response.data.stats.total_payments_count || 0,
          total_invoiced_amount: response.data.stats.total_invoiced_amount || 0,
          total_payments_amount: response.data.stats.total_payments_amount || 0,
          total_amount: response.data.stats.total_amount,
          missing_receipts: response.data.stats.missing_receipts,
          closed: !!response.data.stats.closing,
          gaps: response.data.stats.gaps || [],
          consecutive_range: response.data.stats.consecutive_range || null
        });
      }
    } catch (error) {
      console.error('Error loading billing history:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [date]);

  const loadMonthlyLog = useCallback(async () => {
    setLoadingLog(true);
    try {
      const response = await billingService.getMonthlyLog(selectedMonth);
      if (response.success) {
        setMonthlyLog(response.data);
      }
    } catch (error) {
      console.error('Error loading monthly log:', error);
      toast.error('Error al cargar la bitácora mensual');
    } finally {
      setLoadingLog(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (activeTab === 'audit') {
      loadHistory();
    } else if (activeTab === 'log') {
      loadMonthlyLog();
    }
  }, [date, activeTab, loadHistory, loadMonthlyLog]);

  // Compute delivery type breakdown
  const deliveryBreakdown = useMemo(() => {
    const breakdown = {};
    history.forEach(order => {
      const key = order.delivery_method || '_none';
      if (!breakdown[key]) {
        breakdown[key] = { count: 0, total: 0 };
      }
      breakdown[key].count += 1;
      breakdown[key].total += parseFloat(order.total_amount) || 0;
    });
    // Sort by count descending
    return Object.entries(breakdown)
      .sort((a, b) => b[1].count - a[1].count);
  }, [history]);

  // Compute payment status breakdown
  const paymentBreakdown = useMemo(() => {
    const paid = { count: 0, total: 0 };
    const partial = { count: 0, total: 0 };
    const pending = { count: 0, total: 0 };
    history.forEach(order => {
      const ps = String(order.payment_status || '').toLowerCase();
      const amt = parseFloat(order.total_amount) || 0;
      if (ps === 'completed' || ps === 'overpaid') {
        paid.count += 1;
        paid.total += amt;
      } else if (ps === 'partial') {
        partial.count += 1;
        partial.total += amt;
      } else {
        pending.count += 1;
        pending.total += amt;
      }
    });
    return { paid, partial, pending };
  }, [history]);

  // Compute process status breakdown
  const processBreakdown = useMemo(() => {
    const breakdown = {};
    history.forEach(order => {
      const s = String(order.status || '').toLowerCase();
      if (!breakdown[s]) {
        breakdown[s] = { count: 0, total: 0 };
      }
      breakdown[s].count += 1;
      breakdown[s].total += parseFloat(order.total_amount) || 0;
    });
    return Object.entries(breakdown)
      .sort((a, b) => b[1].count - a[1].count);
  }, [history]);

  // Compute hourly billing activity
  const hourlyBreakdown = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0, total: 0 }));
    history.forEach(order => {
      const h = order.colombia_hour;
      if (h !== null && h !== undefined) {
        hours[h].count += 1;
        hours[h].total += parseFloat(order.total_amount) || 0;
      }
    });
    // Only return hours that have range (first active to last active)
    const activeHours = hours.filter(h => h.count > 0);
    if (activeHours.length === 0) return { hours: [], maxCount: 0, peakHour: null };
    const minHour = activeHours[0].hour;
    const maxHour = activeHours[activeHours.length - 1].hour;
    const rangeHours = hours.slice(minHour, maxHour + 1);
    const maxCount = Math.max(...rangeHours.map(h => h.count));
    const maxTotal = Math.max(...rangeHours.map(h => h.total));
    const peakHour = rangeHours.find(h => h.count === maxCount);
    const moneyPeakHour = rangeHours.find(h => h.total === maxTotal);
    return { hours: rangeHours, maxCount, maxTotal, peakHour, moneyPeakHour };
  }, [history]);

  // Compute per-user breakdown with delivery type detail
  const userBreakdown = useMemo(() => {
    const breakdown = {};
    history.forEach(order => {
      const key = order.managed_by || 'Sistema';
      if (!breakdown[key]) {
        breakdown[key] = { count: 0, total: 0, byDelivery: {} };
      }
      breakdown[key].count += 1;
      breakdown[key].total += parseFloat(order.total_amount) || 0;
      // Per delivery type
      const dm = order.delivery_method || '_none';
      if (!breakdown[key].byDelivery[dm]) {
        breakdown[key].byDelivery[dm] = { count: 0, total: 0 };
      }
      breakdown[key].byDelivery[dm].count += 1;
      breakdown[key].byDelivery[dm].total += parseFloat(order.total_amount) || 0;
    });
    return Object.entries(breakdown)
      .sort((a, b) => b[1].count - a[1].count);
  }, [history]);

  // Filter orders by delivery type
  const filteredHistory = useMemo(() => {
    if (!filterDelivery) return history;
    if (filterDelivery === '_none') return history.filter(o => !o.delivery_method);
    return history.filter(o => o.delivery_method === filterDelivery);
  }, [history, filterDelivery]);

  const handleSaveReceipt = async (orderId) => {
    if (!receiptValue.trim()) {
      toast.error('Ingresa el número de recibo');
      return;
    }
    setSavingReceipt(true);
    try {
      const response = await billingService.updatePisoReceipt(orderId, receiptValue.trim());
      if (response.success) {
        // Update local state without full reload
        setHistory(prev => prev.map(o => 
          o.id === orderId ? { ...o, piso_receipt_number: receiptValue.trim() } : o
        ));
        // Recalculate missing receipts
        setStats(prev => ({
          ...prev,
          missing_receipts: Math.max(0, prev.missing_receipts - 1)
        }));
        toast.success('Recibo guardado');
        setEditingReceipt(null);
        setReceiptValue('');
      }
    } catch (error) {
      console.error('Error saving receipt:', error);
      toast.error('Error guardando recibo');
    } finally {
      setSavingReceipt(false);
    }
  };

  const handleCloseDay = async () => {
    if (stats.missing_receipts > 0) {
      toast.error(`No se puede cerrar el día. Faltan ${stats.missing_receipts} números de recibo de piso.`);
      return;
    }

    if (!window.confirm(`¿Estás seguro de finalizar el día ${date}? Se registrará un total de ${stats.total_orders} pedidos facturados por $${formatCurrencyCOP(stats.total_amount)}.`)) {
      return;
    }

    setClosingInProgress(true);
    try {
      const response = await billingService.closeDay(date);
      if (response.success) {
        toast.success('Día cerrado exitosamente');
        loadHistory();
      }
    } catch (error) {
      console.error('Error closing day:', error);
      toast.error(error.response?.data?.message || 'Error al cerrar el día');
    } finally {
      setClosingInProgress(false);
    }
  };

  const handleReOpen = async () => {
    if (!window.confirm(`¿Estás seguro de RE-ABRIR el día ${date}? El registro de cierre actual será eliminado.`)) {
      return;
    }

    try {
      const response = await billingService.reOpenDay(date);
      if (response.success) {
        toast.success('Día re-abierto exitosamente');
        loadHistory();
      }
    } catch (error) {
      console.error('Error re-opening day:', error);
      toast.error('Error al re-abrir el día');
    }
  };

  const handleMassiveClose = async () => {
    if (!window.confirm('¿Deseas realizar un CIERRE MASIVO de todos los días pendientes anteriores al 18 de Abril? Esta acción regularizará el historial de Febrero y Marzo.')) {
      return;
    }

    setIsMassiveClosing(true);
    try {
      const response = await billingService.massiveClose('2026-04-18');
      if (response.success) {
        toast.success(response.message);
        loadHistory();
      }
    } catch (error) {
      console.error('Error in massive close:', error);
    } finally {
      setIsMassiveClosing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full min-h-[600px]">
      {/* Search & Tabs Top Bar */}
      <div className="bg-white border-b border-gray-200 px-4">
        <div className="flex items-center justify-between">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('audit')}
              className={`py-4 px-1 border-b-2 font-bold text-sm transition-all ${
                activeTab === 'audit' 
                  ? 'border-indigo-600 text-indigo-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icons.ClipboardCheck className="w-4 h-4" />
                <span>Auditoría Diaria</span>
              </div>
            </button>
            {user?.role === 'admin' && (
              <>
                <button
                  onClick={() => setActiveTab('trends')}
                  className={`py-4 px-1 border-b-2 font-bold text-sm transition-all ${
                    activeTab === 'trends' 
                      ? 'border-indigo-600 text-indigo-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Icons.TrendingUp className="w-4 h-4" />
                    <span>Análisis de Tendencias</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('log')}
                  className={`py-4 px-1 border-b-2 font-bold text-sm transition-all ${
                    activeTab === 'log' 
                      ? 'border-indigo-600 text-indigo-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Icons.List className="w-4 h-4" />
                    <span>Bitácora Mensual</span>
                  </div>
                </button>
              </>
            )}
          </div>

          {user?.role === 'admin' && activeTab === 'audit' && (
            <button
              onClick={handleMassiveClose}
              disabled={isMassiveClosing}
              className="flex items-center space-x-2 text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-3 py-1 rounded-full transition-colors"
            >
              <Icons.FastForward className="w-3 h-3" />
              <span>{isMassiveClosing ? 'Cerrando...' : 'Regularizar Historial (Masivo)'}</span>
            </button>
          )}
        </div>
      </div>

      {activeTab === 'log' && user?.role === 'admin' ? (
        <MonthlyLogView 
          data={monthlyLog} 
          loading={loadingLog} 
          onViewDate={(d) => { setDate(d); setActiveTab('audit'); }}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          onRefresh={loadMonthlyLog}
        />
      ) : activeTab === 'trends' && user?.role === 'admin' ? (
        <BillingTrendsTab />
      ) : (
        <>
          {/* Header / Toolbar */}
          <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Icons.Calendar className="w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setFilterDelivery(null); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <button 
            onClick={loadHistory}
            disabled={loading}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Icons.RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex items-center space-x-8">
          <div className="text-right">
            <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest text-right mb-1">Venta del Día (Facturado)</p>
            <p className="text-2xl font-black text-indigo-700 leading-none tracking-tighter">${formatCurrencyCOP(stats.total_invoiced_amount)}</p>
            <div className="flex items-center justify-end space-x-3 mt-1.5 pt-1 border-t border-gray-100">
              <div className="text-[9px] font-bold text-gray-500 uppercase tracking-tighter">
                Cierre Caja: <span className="text-gray-900">${formatCurrencyCOP(stats.total_amount)}</span>
              </div>
              <div className="text-[9px] font-bold text-emerald-600 uppercase tracking-tighter border-l border-gray-200 pl-3">
                Recaudo: <span className="text-emerald-700">${formatCurrencyCOP(stats.total_payments_amount)}</span>
              </div>
            </div>
          </div>
          <div className="text-right border-l border-gray-100 pl-8">
            <p className="text-[10px] uppercase font-black text-gray-400 tracking-widest text-right block mb-1">Documentos</p>
            <div className="flex items-center justify-end space-x-3 h-6">
              <div className="flex items-center space-x-1.5" title="Facturas de hoy">
                <Icons.FileText className="w-3.5 h-3.5 text-indigo-400" />
                <span className="text-sm font-black text-gray-900">{stats.total_orders}</span>
              </div>
              <div className="flex items-center space-x-1.5 border-l border-gray-200 pl-3" title="Recibos de cartera hoy">
                <Icons.Receipt className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-sm font-black text-gray-900">{stats.total_payments_count}</span>
              </div>
            </div>
          </div>
          
          {stats.closed ? (
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-lg font-bold shadow-sm">
                <Icons.CheckCircle className="w-5 h-5" />
                <span>DÍA CERRADO</span>
              </div>
              {user?.role === 'admin' && (
                <button
                  onClick={handleReOpen}
                  className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                  title="Re-abrir día"
                >
                  <Icons.Unlock className="w-5 h-5" />
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handleCloseDay}
              disabled={closingInProgress || loading}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-bold transition-all shadow-sm ${
                stats.missing_receipts > 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white active:scale-95'
              }`}
            >
              <Icons.Flag className="w-5 h-5" />
              <span>{closingInProgress ? 'CERRANDO...' : 'FINALIZAR DÍA'}</span>
            </button>
          )}
        </div>
      </div>

      {stats.missing_receipts > 0 && !stats.closed && (
        <div className="bg-amber-50 border-b border-amber-200 p-3 flex items-center justify-between text-amber-800">
          <div className="flex items-center space-x-2 text-sm font-medium">
            <Icons.AlertTriangle className="w-4 h-4" />
            <span>Hay {stats.missing_receipts} pedidos de bodega sin número de recibo de piso asignado.</span>
          </div>
          <p className="text-[10px] italic">Indispensable para auditoría física</p>
        </div>
      )}

      {stats.gaps && stats.gaps.length > 0 && (
        <div className="bg-red-50 border-b border-red-200 p-3 text-red-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm font-bold">
              <Icons.AlertOctagon className="w-5 h-5 text-red-600" />
              <span>⚠️ Se detectaron {stats.gaps.length} hueco(s) en el consecutivo de facturación</span>
            </div>
            {stats.consecutive_range && (
              <span className="text-[10px] font-bold bg-red-100 px-2 py-1 rounded border border-red-300">
                Rango: {stats.consecutive_range.min} → {stats.consecutive_range.max}
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {stats.gaps.map(g => (
              <span key={g} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black bg-red-100 text-red-800 border border-red-300">
                {g}
              </span>
            ))}
          </div>
          <p className="text-[10px] italic mt-1.5 text-red-600">Estas facturas no aparecen en el sistema para esta fecha. Verificar si fueron anuladas o asignadas a otra fecha.</p>
        </div>
      )}

      {/* Delivery Type Breakdown Cards */}
      {history.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-200 bg-slate-50/50">

          {/* Hourly activity chart */}
          {hourlyBreakdown.hours.length > 0 && (
            <div className="mb-3 pb-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icons.BarChart3 className="w-4 h-4 text-gray-500" />
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actividad por hora ($ y #)</span>
                </div>
                <div className="flex gap-2">
                  {hourlyBreakdown.peakHour && (
                    <span className="text-[10px] font-black text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">
                      📦 Pico Vol: {hourlyBreakdown.peakHour.hour}:00 ({hourlyBreakdown.peakHour.count} ped)
                    </span>
                  )}
                  {hourlyBreakdown.moneyPeakHour && (
                    <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      💰 Pico Dinero: {hourlyBreakdown.moneyPeakHour.hour}:00 ({formatCurrencyAbbreviated(hourlyBreakdown.moneyPeakHour.total)})
                    </span>
                  )}
                </div>
              </div>
              
              <div className="relative" style={{ height: '150px' }}>
                {/* SVG Line Overlay for Orders (#) */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-30" preserveAspectRatio="none">
                  {(() => {
                    const numHours = hourlyBreakdown.hours.length;
                    const points = hourlyBreakdown.hours.map((h, i) => {
                      const x = ((i + 0.5) / numHours) * 100;
                      const maxLineHeight = 80;
                      // Line now represents COUNT (Orders)
                      const yValue = 90 - (hourlyBreakdown.maxCount > 0 ? (h.count / hourlyBreakdown.maxCount) * maxLineHeight : 0);
                      const yPercent = (yValue / 150) * 100;
                      return `${x}% ${yPercent}%`;
                    });
                    
                    if (points.length < 1) return null;
                    
                    return (
                      <>
                        {points.length >= 2 && (
                          <polyline
                            points={points.join(' ')}
                            fill="none"
                            stroke="#4338ca" // Indigo color for Orders (Line)
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            opacity="0.9"
                            style={{ filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.2))' }}
                          />
                        )}
                        {hourlyBreakdown.hours.map((h, i) => {
                          if (h.count === 0 && h.total === 0) return null;
                          const x = ((i + 0.5) / numHours) * 100;
                          const maxLineHeight = 80;
                          const yValue = 90 - (hourlyBreakdown.maxCount > 0 ? (h.count / hourlyBreakdown.maxCount) * maxLineHeight : 0);
                          const yPercent = (yValue / 150) * 100;
                          return (
                            <circle 
                              key={i} 
                              cx={`${x}%`} 
                              cy={`${yPercent}%`} 
                              r="4" 
                              fill="#4338ca" 
                              stroke="white" 
                              strokeWidth="2" 
                            />
                          );
                        })}
                      </>
                    );
                  })()}
                </svg>

                <div className="flex items-end gap-[4px] w-full h-full">
                  {hourlyBreakdown.hours.map(h => {
                    // Bars now represent MONEY ($)
                    const pct = hourlyBreakdown.maxTotal > 0 ? (h.total / hourlyBreakdown.maxTotal) * 100 : 0;
                    const isPeak = h.count === hourlyBreakdown.maxCount && h.count > 0;
                    const isMoneyPeak = h.total === hourlyBreakdown.maxTotal && h.total > 0;
                    
                    return (
                      <div
                        key={h.hour}
                        className="flex-1 flex flex-col items-center group relative h-full"
                      >
                        {/* Tooltip */}
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] font-bold px-3 py-1.5 rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-40">
                          {h.hour}:00 — {formatCurrencyCOP(h.total)} — {h.count} pedidos
                        </div>
                        
                        {/* Bar for Money ($) */}
                        <div className="w-full h-[100px] flex items-end mb-1">
                          <div
                            className={`w-full rounded-t transition-all ${
                              isMoneyPeak ? 'bg-emerald-600' : h.total > 0 ? 'bg-emerald-300' : 'bg-gray-100'
                            }`}
                            style={{ height: `${Math.max(pct, h.total > 0 ? 10 : 2)}%` }}
                          />
                        </div>
                        
                        {/* Labels Area */}
                        <div className="flex flex-col items-center bg-white/90 w-full z-20 pb-1">
                          <span className={`text-[10px] leading-tight mb-0.5 ${
                            isPeak || isMoneyPeak ? 'font-black text-gray-800' : 'font-bold text-gray-400'
                          }`}>
                            {h.hour}h
                          </span>
                          
                          {/* Main Label: Money ($) */}
                          <span className={`text-[11px] font-black leading-none my-0.5 px-1.5 py-0.5 rounded ${
                            isMoneyPeak ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'text-emerald-600 bg-emerald-50/50'
                          }`}>
                            {h.total > 0 ? formatCurrencyAbbreviated(h.total) : '-'}
                          </span>

                          {/* Secondary Label: Orders (#) */}
                          {h.count > 0 && (
                            <span className={`text-[10px] font-black leading-none mt-1 ${
                              isPeak ? 'text-indigo-700' : 'text-gray-500 opacity-80'
                            }`}>
                              {h.count} ped
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            <Icons.Truck className="w-4 h-4 text-gray-500" />
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Desglose por tipo de envío</span>
            {filterDelivery && (
              <button 
                onClick={() => setFilterDelivery(null)}
                className="ml-2 text-[10px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <Icons.X className="w-3 h-3" /> VER TODOS
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {deliveryBreakdown.map(([method, data]) => {
              const cfg = getDeliveryConfig(method === '_none' ? null : method);
              const isActive = filterDelivery === method;
              return (
                <button
                  key={method}
                  onClick={() => setFilterDelivery(filterDelivery === method ? null : method)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all ${
                    isActive 
                      ? 'ring-2 ring-blue-500 shadow-md scale-[1.02]' 
                      : 'hover:shadow-sm hover:scale-[1.01]'
                  } ${cfg.color}`}
                >
                  <span className="text-base">{cfg.icon}</span>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black uppercase tracking-wide leading-none">{cfg.label}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`inline-flex items-center justify-center px-1.5 py-0 rounded text-[9px] font-black text-white ${cfg.badge}`}>
                        {data.count}
                      </span>
                      <span className="text-[11px] font-bold opacity-80">${formatCurrencyCOP(data.total)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Payment status breakdown */}
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Icons.CreditCard className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Estado de pago</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {/* PAGADO */}
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-green-200 bg-green-50 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center">
                  <Icons.CheckCircle className="w-4 h-4 text-green-700" />
                </div>
                <div className="flex flex-col flex-1">
                  <span className="text-[10px] font-black text-green-800 uppercase tracking-wider">Pagado</span>
                  <span className="text-sm font-black text-green-900 leading-tight">${formatCurrencyCOP(paymentBreakdown.paid.total)}</span>
                </div>
                <span className="text-lg font-black text-green-700">{paymentBreakdown.paid.count}</span>
              </div>
              {/* PARCIAL */}
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-amber-200 bg-amber-50 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center">
                  <Icons.Clock className="w-4 h-4 text-amber-700" />
                </div>
                <div className="flex flex-col flex-1">
                  <span className="text-[10px] font-black text-amber-800 uppercase tracking-wider">Parcial</span>
                  <span className="text-sm font-black text-amber-900 leading-tight">${formatCurrencyCOP(paymentBreakdown.partial.total)}</span>
                </div>
                <span className="text-lg font-black text-amber-700">{paymentBreakdown.partial.count}</span>
              </div>
              {/* PENDIENTE */}
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center">
                  <Icons.AlertCircle className="w-4 h-4 text-red-700" />
                </div>
                <div className="flex flex-col flex-1">
                  <span className="text-[10px] font-black text-red-800 uppercase tracking-wider">Pendiente</span>
                  <span className="text-sm font-black text-red-900 leading-tight">${formatCurrencyCOP(paymentBreakdown.pending.total)}</span>
                </div>
                <span className="text-lg font-black text-red-700">{paymentBreakdown.pending.count}</span>
              </div>
            </div>
          </div>

          {/* Process status breakdown */}
          {processBreakdown.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Icons.Activity className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Estado en proceso</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {processBreakdown.map(([status, data]) => {
                  const statusCfg = (() => {
                    const s = status.toLowerCase();
                    if (s === 'entregado_cliente' || s === 'entregado') return { label: 'ENTREGADO', color: 'bg-green-50 border-green-300 text-green-800', icon: '✅' };
                    if (s === 'en_reparto') return { label: 'EN REPARTO', color: 'bg-orange-50 border-orange-300 text-orange-800', icon: '🚚' };
                    if (s === 'entregado_transportadora') return { label: 'EN TRANSPORTADORA', color: 'bg-blue-50 border-blue-300 text-blue-800', icon: '📦' };
                    if (s === 'listo_para_entrega') return { label: 'LISTO PARA ENVÍO', color: 'bg-indigo-50 border-indigo-300 text-indigo-800', icon: '📋' };
                    if (s === 'pendiente_por_facturacion') return { label: 'PTE FACTURACIÓN', color: 'bg-yellow-50 border-yellow-300 text-yellow-800', icon: '🧾' };
                    if (s === 'revision_cartera') return { label: 'EN CARTERA', color: 'bg-purple-50 border-purple-300 text-purple-800', icon: '💳' };
                    if (s === 'por_alistar' || s === 'pendiente') return { label: 'POR ALISTAR', color: 'bg-cyan-50 border-cyan-300 text-cyan-800', icon: '📦' };
                    if (s === 'en_alistamiento') return { label: 'ALISTANDO', color: 'bg-sky-50 border-sky-300 text-sky-800', icon: '🔧' };
                    if (s === 'cancelado' || s === 'cancelado_cliente') return { label: 'CANCELADO', color: 'bg-gray-50 border-gray-300 text-gray-600', icon: '❌' };
                    return { label: s.replace(/_/g, ' ').toUpperCase(), color: 'bg-gray-50 border-gray-300 text-gray-700', icon: '📌' };
                  })();
                  return (
                    <div key={status} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${statusCfg.color}`}>
                      <span className="text-sm">{statusCfg.icon}</span>
                      <div className="flex flex-col">
                        <span className="text-[9px] font-black uppercase tracking-wide leading-none">{statusCfg.label}</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] font-black">{data.count}</span>
                          <span className="text-[10px] font-bold opacity-60">${formatCurrencyCOP(data.total)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* User breakdown row */}
          {userBreakdown.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Icons.Users className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Gestión por usuario</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(userBreakdown.length, 4)}, 1fr)`, gap: '8px' }}>
                {userBreakdown.map(([userName, data]) => {
                  const deliveryEntries = Object.entries(data.byDelivery)
                    .sort((a, b) => b[1].count - a[1].count);
                  return (
                    <div key={userName} className={`flex flex-col gap-1.5 px-3 py-2.5 rounded-lg border shadow-sm ${
                      userName !== 'Sistema' 
                        ? 'bg-white border-indigo-100' 
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      {/* Header: avatar + name + count + total */}
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black border-2 ${
                          userName !== 'Sistema' 
                            ? 'bg-indigo-100 text-indigo-700 border-indigo-300' 
                            : 'bg-gray-100 text-gray-500 border-gray-300'
                        }`}>
                          {userName.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-xs font-bold text-gray-800 truncate">{userName}</span>
                          <span className="text-[10px] font-bold text-gray-400">{data.count} pedidos</span>
                        </div>
                        <span className="text-xs font-black text-gray-700 whitespace-nowrap">${formatCurrencyCOP(data.total)}</span>
                      </div>
                      {/* Delivery type breakdown */}
                      <div className="flex flex-wrap gap-1">
                        {deliveryEntries.map(([dm, dmData]) => {
                          const cfg = getDeliveryConfig(dm === '_none' ? null : dm);
                          return (
                            <div key={dm} className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${cfg.color}`} title={`$${formatCurrencyCOP(dmData.total)}`}>
                              <span className="text-[10px]">{cfg.icon}</span>
                              <span className="text-[9px] font-black">{dmData.count}</span>
                              <span className="text-[9px] font-bold opacity-60">${formatCurrencyCOP(dmData.total)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b border-gray-200">
              <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Orden / Factura</th>
              <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Cliente</th>
              <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Gestión ("Quién")</th>
              <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Envío / Pago</th>
              <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Estado Proceso</th>
              <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center">Recibo Piso</th>
              <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredHistory.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-12 text-center text-gray-400 italic">
                  {loading ? 'Cargando historial...' : 'No se encontraron pedidos facturados para esta fecha.'}
                </td>
              </tr>
            ) : (
              filteredHistory.map((order) => {
                const isBodega = ['recoge_bodega', 'pos'].includes(order.delivery_method);
                const hasReceipt = !!order.piso_receipt_number;
                const requiresAttention = isBodega && !hasReceipt;

                const statusInfo = getStatusDisplay(order.status, order.delivery_method);
                const paymentInfo = getPaymentDisplay(order.payment_method);
                const deliveryCfg = getDeliveryConfig(order.delivery_method);

                return (
                  <tr key={order.id} className={`hover:bg-gray-50 transition-colors ${requiresAttention ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900 leading-tight">#{order.order_number}</span>
                          {order.is_local ? (
                            <span className="bg-amber-100 text-amber-700 text-[8px] font-black px-1 rounded border border-amber-200">LOCAL</span>
                          ) : null}
                        </div>
                        <span className={`text-[10px] font-black uppercase tracking-tighter ${order.is_local ? 'text-gray-400' : 'text-blue-600'}`}>
                          {order.siigo_invoice_number || (order.is_local ? 'Registro Interno' : 'Sin SIIGO')}
                        </span>
                        {order.parent_order_id && (
                          <div className="flex items-center gap-1 mt-0.5 text-[8px] font-bold text-gray-400 uppercase">
                            <Icons.Link className="w-2 h-2" />
                            <span>Asociado a Padre</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-800 line-clamp-1">{order.customer_name}</span>
                        <span className="text-[10px] text-gray-500">{order.customer_identification}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black border ${
                          order.managed_by !== 'Sistema' 
                            ? 'bg-indigo-100 text-indigo-700 border-indigo-200' 
                            : 'bg-gray-100 text-gray-500 border-gray-200'
                        }`}>
                          {(order.managed_by || 'S').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[11px] font-bold text-gray-700">{order.managed_by || 'Sistema'}</span>
                          {order.managed_at && (
                            <span className="text-[9px] text-gray-400 font-medium">
                              {new Date(order.managed_at).toLocaleTimeString('es-CO', { 
                                hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Bogota' 
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black w-fit border ${deliveryCfg.color}`}>
                          {deliveryCfg.icon} {deliveryCfg.label}
                        </span>
                        <div className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black border uppercase shadow-sm ${paymentInfo.color}`}>
                          <paymentInfo.icon className="w-3 h-3 mr-1" />
                          <span>{paymentInfo.label}</span>
                        </div>
                        {/* Payment consolidation status */}
                        {(() => {
                          const ps = String(order.payment_status || '').toLowerCase();
                          if (ps === 'completed' || ps === 'overpaid') {
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black bg-green-100 text-green-800 border border-green-300 w-fit">
                                <Icons.CheckCircle className="w-3 h-3" /> PAGADO
                              </span>
                            );
                          }
                          if (ps === 'partial') {
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black bg-amber-100 text-amber-800 border border-amber-300 w-fit">
                                <Icons.Clock className="w-3 h-3" /> PARCIAL
                              </span>
                            );
                          }
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black bg-red-50 text-red-700 border border-red-200 w-fit">
                              <Icons.AlertCircle className="w-3 h-3" /> PENDIENTE
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded text-[10px] font-black uppercase shadow-sm ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isBodega ? (
                        hasReceipt ? (
                          editingReceipt === order.id ? (
                            <div className="inline-flex items-center gap-1">
                              <input
                                type="text"
                                value={receiptValue}
                                onChange={(e) => setReceiptValue(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveReceipt(order.id)}
                                className="w-20 px-2 py-1 border-2 border-emerald-400 rounded text-xs font-bold text-center focus:ring-2 focus:ring-emerald-500 outline-none"
                                autoFocus
                              />
                              <button onClick={() => handleSaveReceipt(order.id)} disabled={savingReceipt} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded">
                                <Icons.Check className="w-4 h-4" />
                              </button>
                              <button onClick={() => { setEditingReceipt(null); setReceiptValue(''); }} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                <Icons.X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingReceipt(order.id); setReceiptValue(order.piso_receipt_number || ''); }}
                              className="inline-flex items-center space-x-1.5 px-3 py-1 bg-white text-emerald-700 border-2 border-emerald-500 rounded-lg font-black text-xs shadow-sm hover:bg-emerald-50 transition-colors cursor-pointer"
                              title="Click para editar"
                            >
                              <Icons.FileText className="w-3.5 h-3.5" />
                              <span>{order.piso_receipt_number}</span>
                              <Icons.Pencil className="w-3 h-3 opacity-40" />
                            </button>
                          )
                        ) : (
                          editingReceipt === order.id ? (
                            <div className="inline-flex items-center gap-1">
                              <input
                                type="text"
                                value={receiptValue}
                                onChange={(e) => setReceiptValue(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveReceipt(order.id)}
                                placeholder="Ej: 1234"
                                className="w-20 px-2 py-1 border-2 border-red-300 rounded text-xs font-bold text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-400 outline-none"
                                autoFocus
                              />
                              <button onClick={() => handleSaveReceipt(order.id)} disabled={savingReceipt} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                                <Icons.Check className="w-4 h-4" />
                              </button>
                              <button onClick={() => { setEditingReceipt(null); setReceiptValue(''); }} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                <Icons.X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingReceipt(order.id); setReceiptValue(''); }}
                              className="inline-flex flex-col items-center cursor-pointer hover:scale-105 transition-transform group"
                              title="Click para asignar recibo de piso"
                            >
                              <Icons.AlertCircle className="w-5 h-5 text-red-500 mb-0.5 animate-pulse group-hover:animate-none" />
                              <span className="text-[9px] font-black text-red-600 uppercase group-hover:text-blue-600">ASIGNAR</span>
                            </button>
                          )
                        )
                      ) : (
                        <span className="text-gray-300 text-[10px] font-bold italic">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-black text-gray-900 text-sm whitespace-nowrap">${formatCurrencyCOP(order.total_amount)}</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {/* Table footer with filtered totals */}
          {filteredHistory.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td colSpan="5" className="px-4 py-2.5 text-right">
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-wider">
                    {filterDelivery ? `Subtotal ${getDeliveryConfig(filterDelivery === '_none' ? null : filterDelivery).label}` : 'Total General'}
                    {' '}({filteredHistory.length} pedidos)
                  </span>
                </td>
                <td className="px-4 py-2.5"></td>
                <td className="px-4 py-2.5 text-right">
                  <span className="font-black text-gray-900 text-sm">
                    ${formatCurrencyCOP(filteredHistory.reduce((sum, o) => sum + (parseFloat(o.total_amount) || 0), 0))}
                  </span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Collections Section (Pagos de Cartera) */}
      {payments.length > 0 && (
        <div className="mt-8 border-t border-gray-200 pt-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="p-1 px-2 bg-emerald-100 text-emerald-700 text-[10px] font-black rounded border border-emerald-200 uppercase tracking-tighter">Recaudo Cartera</div>
              <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest">Abonos y Pagos de Facturas Anteriores</h4>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold text-gray-400 uppercase mr-2">Subtotal Recaudo:</span>
              <span className="text-sm font-black text-emerald-700">${formatCurrencyCOP(stats.total_payments_amount)}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-2.5 text-left text-[9px] font-black text-gray-500 uppercase tracking-wider">Orden Ref.</th>
                  <th className="px-4 py-2.5 text-left text-[9px] font-black text-gray-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-4 py-2.5 text-left text-[9px] font-black text-gray-500 uppercase tracking-wider">Método Pago</th>
                  <th className="px-4 py-2.5 text-left text-[9px] font-black text-gray-500 uppercase tracking-wider">Referencia / Notas</th>
                  <th className="px-4 py-2.5 text-left text-[9px] font-black text-gray-500 uppercase tracking-wider">Gestionado Por</th>
                  <th className="px-4 py-2.5 text-right text-[9px] font-black text-gray-500 uppercase tracking-wider font-right">Monto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.map((p) => (
                  <tr key={p.id} className="hover:bg-emerald-50/30 transition-colors">
                    <td className="px-4 py-2.5 text-xs font-bold text-gray-700 italic">#{p.order_number}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs font-black text-gray-900 leading-tight block">{p.customer_name}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[9px] font-black uppercase border border-gray-200">
                        {p.payment_method === 'efectivo' ? 'Efectivo Bodega' : p.payment_method.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[10px] text-gray-500 font-medium italic max-w-xs truncate">
                      {p.reference || p.notes || '-'}
                    </td>
                    <td className="px-4 py-2.5 text-[10px] text-gray-500 font-bold">{p.managed_by || 'Sistema'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="font-black text-gray-900 text-sm whitespace-nowrap">${formatCurrencyCOP(p.amount)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )}
</div>
  );
};

export default BillingHistoryTab;
